import type { ReactNode } from 'react';
import {
  LOG_SOURCE_LABELS,
  PACKAGE_EVENT_LABELS,
  describeCondominiumAction,
  describePlatformAction,
  type LogSource,
  type PackageEventType,
  type SystemLogEntry,
} from '@azko/shared';

import { cx } from './ui.js';

/**
 * A lista de log — a mesma para o sindico e para a Azko.
 *
 * As duas telas mostravam a mesma coisa de dois jeitos diferentes, e nenhum dos
 * dois respondia a pergunta que se faz olhando um log: *quando* foi isso. Uma
 * coluna de data por linha, repetindo "24/08/2026" trinta vezes, gasta a
 * largura toda para dizer o que muda uma vez por dia.
 *
 * Aqui o dia vira cabecalho e a linha guarda so a hora. O que sobra de espaco
 * vai para o que de fato distingue uma linha da outra: quem fez, em qual
 * condominio, e o detalhe.
 */

function dayKey(iso: string): string {
  return iso.slice(0, 10);
}

const TODAY = new Date().toISOString().slice(0, 10);
const YESTERDAY = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);

function describeDay(key: string): string {
  if (key === TODAY) return 'Hoje';
  if (key === YESTERDAY) return 'Ontem';

  return new Date(`${key}T12:00:00Z`).toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

/**
 * O rotulo da acao, sabendo de qual trilha ela veio.
 *
 * Cada trilha tem o proprio dicionario no `shared` e nenhuma conhece as outras
 * — juntar os tres la embaixo criaria dependencia circular entre os modulos.
 * Aqui em cima, que e apresentacao, a escolha e trivial.
 */
export function describeLogAction(source: LogSource, action: string): string {
  if (source === 'plataforma') return describePlatformAction(action);
  if (source === 'condominio') return describeCondominiumAction(action);

  const type = action.replace(/^encomenda\./, '') as PackageEventType;
  return PACKAGE_EVENT_LABELS[type] ?? action;
}

const SOURCE_TONE: Record<LogSource, string> = {
  plataforma: 'bg-accent/15 text-accent',
  condominio: 'bg-ok/15 text-ok',
  encomenda: 'bg-warn/15 text-warn',
};

/**
 * O `metadata` de cada acao, em uma linha.
 *
 * E ele que responde a segunda pergunta de quem le. "Moradores importados" diz
 * o que houve; "linhas: 405 - moradores: 478 - erros: 0" diz se deu certo.
 */
function describeMetadata(metadata: Record<string, unknown> | null): string | null {
  if (!metadata) return null;

  const parts = Object.entries(metadata)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => {
      const label = key.replace(/_/g, ' ');
      const shown = Array.isArray(value) ? value.join(', ') : String(value);
      // Metadata e JSON livre: um campo enorme nao pode empurrar o resto da
      // linha para fora da tela.
      return `${label}: ${shown.length > 60 ? `${shown.slice(0, 60)}...` : shown}`;
    });

  return parts.length > 0 ? parts.join(' • ') : null;
}

interface LogFeedProps {
  entries: SystemLogEntry[];
  /** A Azko ve de qual condominio veio a linha; o sindico ja sabe. */
  showCondominium?: boolean;
  /** A origem so faz sentido onde as tres trilhas convivem. */
  showSource?: boolean;
  footer?: ReactNode;
}

export function LogFeed({
  entries,
  showCondominium = false,
  showSource = false,
  footer,
}: LogFeedProps) {
  const days: Array<{ key: string; items: SystemLogEntry[] }> = [];
  for (const entry of entries) {
    const key = dayKey(entry.createdAt);
    const current = days[days.length - 1];
    if (current?.key === key) current.items.push(entry);
    else days.push({ key, items: [entry] });
  }

  return (
    <div>
      {days.map((day) => (
        <section key={day.key}>
          {/* Gruda no topo ao rolar: numa lista longa, saber em que dia voce
              esta e a unica coisa que se perde primeiro. */}
          <h3 className="sticky top-0 z-10 border-y border-line-soft bg-surface/95 px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted backdrop-blur sm:px-6">
            {describeDay(day.key)}
          </h3>

          <ul className="divide-y divide-line-soft">
            {day.items.map((entry) => {
              const detail = describeMetadata(entry.metadata);

              return (
                <li key={entry.id} className="flex gap-3 px-4 py-3 sm:gap-4 sm:px-6 sm:py-4">
                  {/* A hora primeiro, em largura fixa: o olho desce por essa
                      coluna procurando "por volta das 14h" sem reler o resto. */}
                  <span className="w-11 shrink-0 pt-0.5 text-sm tabular-nums text-muted">
                    {formatTime(entry.createdAt)}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      {showSource && (
                        <span
                          className={cx(
                            'rounded-pill px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider',
                            SOURCE_TONE[entry.source],
                          )}
                        >
                          {LOG_SOURCE_LABELS[entry.source]}
                        </span>
                      )}
                      <span className="font-bold">
                        {describeLogAction(entry.source, entry.action)}
                      </span>
                      {entry.detail && (
                        <span className="text-sm text-muted">· {entry.detail}</span>
                      )}
                    </div>

                    <p className="mt-0.5 break-words text-sm text-muted">
                      {showCondominium && entry.condominiumName && (
                        <>
                          <span className="text-fg">{entry.condominiumName}</span>
                          {' · '}
                        </>
                      )}
                      por <span className="text-fg">{entry.actorName || 'sistema'}</span>
                      {entry.actorRole && ` (${entry.actorRole})`}
                    </p>

                    {/* O motivo nao fica atras de um clique: sem ele a linha diz
                        o que houve mas nao por que, que e a parte que o
                        atendimento precisa. */}
                    {entry.reason && (
                      <p className="mt-1 break-words text-sm text-faint">{entry.reason}</p>
                    )}
                    {detail && <p className="mt-1 break-words text-sm text-faint">{detail}</p>}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      {footer}
    </div>
  );
}
